import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@/shared/ui'

export interface FieldOption {
  key: string
  label: string
}

export const FIELD_OPTIONS: FieldOption[] = [
  { key: 'last_name', label: 'Фамилия' },
  { key: 'first_name', label: 'Имя' },
  { key: 'middle_name', label: 'Отчество' },
  { key: 'email', label: 'Email' },
  { key: 'inn', label: 'ИНН' },
  { key: 'passport', label: 'Паспорт' },
  { key: 'profile_image', label: 'Фото профиля' },
  { key: 'passport_image_first', label: 'Паспорт (1 стр.)' },
  { key: 'passport_image_registration', label: 'Паспорт (регистрация)' },
  { key: 'region', label: 'Регион' },
  { key: 'activity_types', label: 'Вид деятельности' },
  { key: 'transport_types', label: 'Вид транспорта' },
  { key: 'driver_license', label: 'Вод. удостоверение' },
  { key: 'driver_license_image', label: 'Фото ВУ' },
  { key: 'delivery_radius', label: 'Радиус доставки' },
  { key: 'delivery_address', label: 'Адрес доставки' },
]

interface RejectModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (reason: string, fields: string[]) => void | Promise<void>
  courierName?: string
  loading?: boolean
}

export const RejectModal: React.FC<RejectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  courierName,
  loading = false,
}) => {
  const [reason, setReason] = useState('')
  const [selectedFields, setSelectedFields] = useState<string[]>([])

  useEffect(() => {
    if (isOpen) {
      setReason('')
      setSelectedFields([])
    }
  }, [isOpen])

  if (!isOpen) return null

  const toggleField = (key: string) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((field) => field !== key) : [...prev, key]
    )
  }

  const handleSubmit = () => {
    const trimmed = reason.trim()
    if (!trimmed) return
    void onSubmit(trimmed, selectedFields)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Отклонить курьера{courierName ? ` «${courierName}»` : ''}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Причина отклонения */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Причина отклонения <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="Опишите причину отклонения..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
            />
            <div className="text-right text-xs text-gray-400 mt-1">
              {reason.length}/500
            </div>
          </div>

          {/* Поля к исправлению */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Поля к исправлению
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Отметьте поля, которые курьеру нужно исправить
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {FIELD_OPTIONS.map((option) => (
                <label
                  key={option.key}
                  className="flex items-center px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(option.key)}
                    onChange={() => toggleField(option.key)}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            isLoading={loading}
            disabled={!reason.trim()}
          >
            Отклонить
          </Button>
        </div>
      </div>
    </div>
  )
}
